import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiEye, FiEyeOff, FiCopy } from "react-icons/fi";
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import premiumBack from "../../assets/images/premiumback.png";
import "./Card.css";
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import { useLanguage } from "../../contexts/LanguageContext";


export default function Card() {
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [hasCard, setHasCard] = useState(false);
  const [cardNumber, setCardNumber] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [activating, setActivating] = useState(false);
  const { translations: t } = useLanguage();


  // Generate random card number and CVV
  const generateCardData = () => {
    // Generate 16-digit card number
    const cardNum = Array.from({length: 16}, () => Math.floor(Math.random() * 10)).join('');
    // Generate 3-digit CVV
    const cvv = Array.from({length: 3}, () => Math.floor(Math.random() * 10)).join('');
    return { cardNum, cvv };
  };

  // Activate card function
  const activateCard = async () => {
    if (!userProfile?.uid) return;
    
    setActivating(true);
    try {
      const { cardNum, cvv } = generateCardData();
      
      // Update user document with card data
      const userRef = doc(db, 'users', userProfile.uid);
      await updateDoc(userRef, {
        wintoCardCvv: cvv,
        wintoCardNb: cardNum
      });
      
      // Update local state
      setCardNumber(cardNum);
      setCardCvv(cvv);
      setHasCard(true);
    } catch (error) {
      console.error('Error activating card:', error);
    } finally {
      setActivating(false);
    }
  };

  // Copy card number to clipboard
  const copyCardNumber = async () => {
    if (!revealed || !cardNumber) return;
    
    try {
      const normalizedNum = cardNumber.replace(/\D/g, "").match(/.{1,4}/g)?.join(" ") || cardNumber;
      await navigator.clipboard.writeText(normalizedNum);
      // You could add a toast notification here
    } catch (error) {
      console.error('Error copying card number:', error);
    }
  };

  const normalizedNumber = useMemo(() => {
    // group into 4-4-4-4 for display
    const digits = String(cardNumber).replace(/\D/g, "").slice(0, 16);
    const groups = digits.match(/.{1,4}/g) || [];
    return groups.join(" ").trim();
  }, [cardNumber]);

  const maskedNumber = useMemo(() => {
    return "**** **** **** ****";
  }, [normalizedNumber]);

  const maskedCvv = useMemo(() => (cardCvv ? "***" : "***"), [cardCvv]);

  // Fetch user data and card info
  useEffect(() => {
    // Mark wallet as verified when entering card page
    sessionStorage.setItem('walletVerified', 'true');
    
    const unsub = onAuthStateChanged(getAuth(), async (user) => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch user profile
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserProfile({ ...userData, uid: user.uid });
          
          // Check if user has card data
          if (userData.wintoCardNb && userData.wintoCardCvv) {
            setCardNumber(userData.wintoCardNb);
            setCardCvv(userData.wintoCardCvv);
            setHasCard(true);
          } else {
            setHasCard(false);
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  if (loading) {
    return (
      <div className="card-page">
      <div className="card-loading">{t.card?.loading || "Loading..."}</div>
      </div>
    );
  }

  // No card state
  if (!hasCard) {
    return (
      <div className="card-page">
        <FloatingSidebarButton userProfile={{ name: userProfile?.name || getAuth().currentUser?.displayName, profileImage: userProfile?.profileImage || getAuth().currentUser?.photoURL }} />
        <button
          className="pages-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Back"
          title="Back"
        >
          <FiArrowLeft />
        </button>
        {/* Top bar */}
        <div className="card-topbar">
        <h1 className="card-title">{t.card?.title || "Winto Card"}</h1>
          <div className="card-topbar-spacer" />
        </div>

        {/* No card content */}
        <div className="no-card-container">
          <div className="no-card-icon">💳</div>
          <h2 className="no-card-title">{t.card?.noCardTitle || "You don't have a card"}</h2>
          <p className="no-card-description">{t.card?.noCardDesc || "Do you wish to activate your card?"}</p>
          <button 
            className="activate-card-btn"
            onClick={activateCard}
            disabled={activating}
          >
               {activating ? (t.card?.activating || "Activating...") : (t.card?.activate || "Activate Card")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card-page">
      <FloatingSidebarButton userProfile={{ name: userProfile?.name || getAuth().currentUser?.displayName, profileImage: userProfile?.profileImage || getAuth().currentUser?.photoURL }} />
      <button
        className="pages-back-btn"
        onClick={() => navigate(-1)}
        aria-label="Back"
        title="Back"
      >
        <FiArrowLeft />
      </button>
      {/* Top bar */}
      <div className="card-topbar">
      <h1 className="card-title">{t.card?.title || "Winto Card"}</h1>
        <div className="card-topbar-spacer" />
      </div>

      {/* Centered card */}
      <div className="card-wrap">
        <div className="credit-card">
          <img src={premiumBack} alt="Card background" className="card-bg" />
          <div className="card-overlay" />

                     {/* Eye toggle and Copy button */}
           <div className="card-controls">
             <button
               className="reveal-btn"
               onClick={() => setRevealed((v) => !v)}
               aria-label={revealed ? (t.card?.reveal?.hide || "Hide details") : (t.card?.reveal?.show || "Show details")}
               title={revealed ? (t.card?.reveal?.hide || "Hide details") : (t.card?.reveal?.show || "Show details")}
             >
               {revealed ? <FiEyeOff /> : <FiEye />}
             </button>
             
             {revealed && (
               <button 
                 className="copy-card-btn"
                 onClick={copyCardNumber}
                 title={t.card?.copy || "Copy card number"}

               >
                 <FiCopy />
               </button>
             )}
           </div>

           {/* Number */}
           <div className="card-number">
             {revealed ? normalizedNumber : maskedNumber}
           </div>

          {/* CVV */}
          <div className="card-cvv">
            CVV {revealed ? String(cardCvv).padEnd(3, "•") : maskedCvv}
          </div>

          {/* Name */}
          <div className="card-holder">{userProfile?.name || 'User'}</div>
        </div>

        {/* Soft drop shadow plume under the card */}
        <div className="card-plume" />
      </div>
    </div>
  );
}
